import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SECTIONS, POPULAR_GUIDES, HELP_PDF_URL, findSection } from '@/lib/helpCenter';
import {
  Search, Download, ChevronDown, ArrowLeft, LifeBuoy, Phone, Mail, MessageSquare, ArrowRight,
  UserPlus, PlusCircle, Map, Crown, Calculator, Wrench, ShieldCheck, Smartphone, ListChecks,
} from 'lucide-react';

const ICONS = { UserPlus, PlusCircle, Search, Map, Phone, Crown, Calculator, Wrench, ShieldCheck, Smartphone };

const Accordion = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`help-faq-${idx}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50" data-testid={`help-faq-toggle-${idx}`}>
        <span className="font-semibold text-[#0F172A]">{q}</span>
        <ChevronDown className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5 text-slate-600 leading-relaxed text-[15px]" data-testid={`help-faq-answer-${idx}`}>{a}</div>}
    </div>
  );
};

const StepGuide = ({ guide }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="help-step-guide">
    <h3 className="text-lg font-bold text-[#0F172A] mb-5">{guide.title}</h3>
    <ol className="space-y-4">
      {guide.steps.map((s, i) => (
        <li key={i} className="flex gap-4">
          <span className="flex-shrink-0 h-8 w-8 rounded-full bg-[#0F172A] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
          <div><p className="font-semibold text-[#0F172A]">{s.t}</p><p className="text-sm text-slate-600 mt-0.5">{s.d}</p></div>
        </li>
      ))}
    </ol>
  </div>
);

const DownloadBar = () => (
  <a href={HELP_PDF_URL} target="_blank" rel="noopener noreferrer" download
     className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D4AF37] text-[#0F172A] font-bold hover:bg-[#e0bf48] transition-colors" data-testid="help-download-pdf">
    <Download className="h-4 w-4" /> Download Complete Help Guide (PDF)
  </a>
);

const ContactSupport = () => (
  <div className="bg-[#0F172A] text-white rounded-2xl p-7" data-testid="help-contact-support">
    <h3 className="text-xl font-bold mb-2">Still need help?</h3>
    <p className="text-white/70 text-sm mb-5">Our team is here for you. Reach out any time.</p>
    <div className="flex flex-wrap gap-3">
      <a href="tel:+919465024516"><Button className="rounded-full bg-white text-[#0F172A] hover:bg-slate-100"><Phone className="h-4 w-4 mr-2" /> +91 94650 24516</Button></a>
      <a href="mailto:contact@bharat.estate"><Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10"><Mail className="h-4 w-4 mr-2" /> contact@bharat.estate</Button></a>
      <Link to="/grievance"><Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10"><MessageSquare className="h-4 w-4 mr-2" /> Raise a grievance</Button></Link>
    </div>
  </div>
);

const HelpCenterPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const section = slug ? findSection(slug) : null;

  useEffect(() => { if (slug && !section) navigate('/help-center', { replace: true }); }, [slug, section, navigate]);

  // Global FAQ search (hub only)
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    SECTIONS.forEach((s) => s.faqs.forEach((f) => {
      if (f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) out.push({ ...f, section: s });
    }));
    return out.slice(0, 20);
  }, [query]);

  // FAQ schema for SEO
  const faqSchema = useMemo(() => {
    const faqs = section ? section.faqs : SECTIONS.flatMap((s) => s.faqs).slice(0, 30);
    return {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    };
  }, [section]);

  // ---------- TOPIC PAGE ----------
  if (section) {
    const Icon = ICONS[section.icon] || LifeBuoy;
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-20">
        <Seo title={`${section.title} — Bharat Estate Help Center`} description={section.blurb} path={`/help-center/${section.slug}`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        <div className="bg-[#0F172A] text-white">
          <div className="container-luxury py-10">
            <Link to="/help-center" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4" data-testid="help-back-link"><ArrowLeft className="h-4 w-4" /> Help Center</Link>
            <div className="flex items-center gap-4">
              <span className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center"><Icon className="h-7 w-7 text-[#D4AF37]" /></span>
              <div><h1 className="text-2xl md:text-4xl font-extrabold">{section.title}</h1><p className="text-white/70 mt-1">{section.blurb}</p></div>
            </div>
          </div>
        </div>

        <div className="container-luxury py-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {section.guides?.length > 0 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0F172A] flex items-center gap-2"><ListChecks className="h-5 w-5 text-[#D4AF37]" /> Step-by-step guides</h2>
                {section.guides.map((g, i) => <StepGuide key={i} guide={g} />)}
                <p className="text-sm text-slate-500">Prefer screenshots? The complete illustrated guide with numbered screenshots is in the downloadable PDF.</p>
                <DownloadBar />
              </div>
            )}
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-[#0F172A]">Frequently asked questions</h2>
              {section.faqs.map((f, i) => <Accordion key={i} q={f.q} a={f.a} idx={i} />)}
            </div>
          </div>
          <aside className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">All topics</p>
              <ul className="space-y-1">
                {SECTIONS.map((s) => (
                  <li key={s.slug}><Link to={`/help-center/${s.slug}`} className={`block px-3 py-2 rounded-lg text-sm ${s.slug === section.slug ? 'bg-[#0F172A] text-white font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}>{s.title}</Link></li>
                ))}
              </ul>
            </div>
            <ContactSupport />
          </aside>
        </div>
      </div>
    );
  }

  // ---------- HUB PAGE ----------
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-20">
      <Seo title="Bharat Estate Help Center — FAQs & Guides" description="Find answers fast: step-by-step guides, 100+ FAQs and a doubt resolver for listing, searching, Premium, calculators, home services and more." path="/help-center" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="bg-[#0F172A] text-white">
        <div className="container-luxury py-14 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold mb-3"><LifeBuoy className="h-4 w-4" /> Help Center</span>
          <h1 className="text-3xl md:text-5xl font-extrabold">Bharat Estate Help Center</h1>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto">FAQs, step-by-step guides & doubt resolver.</p>
          <div className="relative max-w-xl mx-auto mt-7">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="How can we help?" className="h-14 pl-12 rounded-full text-[#0F172A] bg-white border-0 text-base" data-testid="help-search-input" />
          </div>
          <div className="mt-5"><DownloadBar /></div>
        </div>
      </div>

      <div className="container-luxury py-10">
        {query.trim() ? (
          <div className="max-w-3xl mx-auto space-y-3" data-testid="help-search-results">
            <p className="text-sm text-slate-500 mb-2">{searchResults.length} result{searchResults.length === 1 ? '' : 's'} for "{query}"</p>
            {searchResults.length === 0 && <p className="text-slate-500">No matches. Try different words, or <a href="mailto:contact@bharat.estate" className="text-[#0F172A] font-semibold underline">email support</a>.</p>}
            {searchResults.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <Link to={`/help-center/${f.section.slug}`} className="text-[11px] font-semibold uppercase tracking-wide text-[#B5952F]">{f.section.title}</Link>
                <p className="font-semibold text-[#0F172A] mt-1">{f.q}</p>
                <p className="text-sm text-slate-600 mt-1">{f.a}</p>
              </div>
            ))}
          </div>
        ) : (
          <>
            <section className="mb-12">
              <h2 className="text-xl font-bold text-[#0F172A] mb-5">Popular guides</h2>
              <div className="flex flex-wrap gap-3" data-testid="help-popular-guides">
                {POPULAR_GUIDES.map((g, i) => (
                  <Link key={i} to={`/help-center/${g.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm font-medium text-[#0F172A] hover:border-[#0F172A] hover:shadow-sm transition-all">
                    {g.label} <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                  </Link>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl font-bold text-[#0F172A] mb-5">Browse by topic</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="help-topic-grid">
                {SECTIONS.map((s) => {
                  const Icon = ICONS[s.icon] || LifeBuoy;
                  return (
                    <Link key={s.slug} to={`/help-center/${s.slug}`} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid={`help-topic-${s.slug}`}>
                      <span className="h-12 w-12 rounded-xl bg-[#FBFAF6] border border-slate-100 flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-[#0F172A]" /></span>
                      <h3 className="font-bold text-[#0F172A] group-hover:text-[#B5952F]">{s.title}</h3>
                      <p className="text-sm text-slate-500 mt-1.5">{s.blurb}</p>
                      <p className="text-xs text-slate-400 mt-3">{s.faqs.length} FAQs →</p>
                    </Link>
                  );
                })}
              </div>
            </section>

            <ContactSupport />
          </>
        )}
      </div>
    </div>
  );
};

export default HelpCenterPage;
